"use client";

import { useEffect, useCallback } from "react";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import {
  Download,
  X,
  Share2,
  RotateCcw,
  ExternalLink,
  Lock,
} from "lucide-react";
import { useCheckout } from "@/hooks/use-checkout";
import { toast } from "sonner";
import {
  isPromotionEnabled,
  getEffectiveCredits,
  PREVIEW_CTA_PACK_ID,
  getPackPriceUsd,
  type CreditPackId,
} from "@/lib/payments/client";
import { MiniPreviewOffer } from "@/components/app/mini-preview-offer";
import { trackFreeExit, trackUpgradePromptShown as trackUpgradePromptShownGA } from "@/lib/analytics";
import {
  FREE_PREVIEW_OFFER_VARIANT,
  getCompletedFreePreviewNumber,
} from "@/lib/payments/free-preview-config";
import { 
  trackDownloadGateClicked,
  trackImageDownloaded, 
  trackUnlockClicked,
  trackFullscreenResultViewed,
  trackFullscreenResultClosed,
  trackShareClicked,
  trackUpgradePromptShown,
  trackFreePreviewOfferShown,
  trackFreePreviewPaidCtaClicked,
} from "@/lib/posthog-client";

interface FullscreenResultModalProps {
  isOpen: boolean;
  onClose: () => void;
  resultImageUrl: string;
  onTryAnother: () => void;
  isFreePreview?: boolean;
  isUnlocked?: boolean;
  generationId?: string;
  onUnlock?: () => void;
  creditBalance?: number;
  hasPurchasedAnyPack?: boolean;
  remainingFreePreviews?: number;
  wheelInfo?: {
    name: string;
    imageUrl: string;
    brand?: string;
    sku?: string;
    isCustom: boolean;
  } | null;
  generationDate?: Date | null;
}

export function FullscreenResultModal({
  isOpen,
  onClose,
  resultImageUrl,
  onTryAnother,
  isFreePreview = false,
  isUnlocked = true,
  generationId,
  onUnlock,
  creditBalance = 0,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  hasPurchasedAnyPack = false,
  remainingFreePreviews = 0,
  wheelInfo,
  generationDate,
}: FullscreenResultModalProps) {
  const checkout = useCheckout();
  const isLocked = isFreePreview && !isUnlocked;
  const canUnlock = creditBalance >= 1;
  const isPromoEnabled = isPromotionEnabled(PREVIEW_CTA_PACK_ID);
  const promoCredits = getEffectiveCredits(PREVIEW_CTA_PACK_ID);
  const paidPreviewsAfterUnlock = Math.max(promoCredits - 1, 0);
  const ctaPackPrice = getPackPriceUsd(PREVIEW_CTA_PACK_ID);
  const previewNumber = getCompletedFreePreviewNumber(remainingFreePreviews);

  // Track when modal is opened
  useEffect(() => {
    if (isOpen) {
      trackFullscreenResultViewed({
        generationId,
        isFreePreview,
        isUnlocked,
      });
      if (isLocked) {
        trackUpgradePromptShownGA({
          trigger: "after_generation",
          isFreePreview,
        });
        trackUpgradePromptShown({
          generationId,
          trigger: "after_generation",
          isFreePreview,
          source: "fullscreen_modal",
        });
        trackFreePreviewOfferShown({
          generationId,
          previewNumber,
          remainingFreePreviews,
          source: "fullscreen_modal",
          offerVariant: FREE_PREVIEW_OFFER_VARIANT,
        });
      }
    }
  }, [
    isOpen,
    generationId,
    isFreePreview,
    isUnlocked,
    isLocked,
    previewNumber,
    remainingFreePreviews,
  ]);

  const handleClose = useCallback(() => {
    trackFullscreenResultClosed({ generationId, action: "close" });
    onClose();
  }, [generationId, onClose]);

  const handleDownload = useCallback(() => {
    if (isLocked) {
      trackFreeExit({
        exitPoint: "download_attempt",
        ...(generationId && { generationId }),
      });
      trackDownloadGateClicked({
        generationId,
        isFreePreview,
        isUnlocked,
        source: "fullscreen_modal",
      });
      return;
    }
    try {
      if (!generationId) {
        toast.error("Unable to download this preview yet");
        return;
      }
      trackImageDownloaded({
        generationId,
        imageType: "result",
        isFreePreview,
        isUnlocked,
      });
      const downloadUrl = `/api/download?generationId=${encodeURIComponent(generationId)}`;
      window.location.href = downloadUrl;
    } catch (error) {
      console.error("Download failed:", error);
    }
  }, [isLocked, generationId, isFreePreview, isUnlocked]);

  const copyLinkFallback = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied to clipboard!");
    } catch {
      toast.error("Failed to copy link");
    }
  }, []);

  const handleShare = useCallback(async () => {
    if (navigator.share) {
      trackShareClicked({ generationId, shareMethod: "native" });
      try {
        await navigator.share({
          title: "Check out my wheel preview!",
          text: "I visualized new wheels on my car with The Wheel Visualizer",
          url: window.location.href,
        });
      } catch (error) {
        if ((error as Error).name !== "AbortError") {
          await copyLinkFallback();
        }
      }
    } else {
      trackShareClicked({ generationId, shareMethod: "copy_link" });
      await copyLinkFallback();
    }
  }, [generationId, copyLinkFallback]);

  const handleUnlock = useCallback(() => {
    if (isLocked) {
      trackFreeExit({
        exitPoint: "unlock_attempt",
        ...(generationId && { generationId }),
      });
      trackUnlockClicked({
        generationId,
        creditBalance,
      });
    }
    if (onUnlock) {
      onUnlock();
    }
  }, [isLocked, generationId, creditBalance, onUnlock]);

  const handlePurchase = useCallback((packId: CreditPackId = PREVIEW_CTA_PACK_ID) => {
    trackFreePreviewPaidCtaClicked({
      packId,
      generationId,
      previewNumber,
      remainingFreePreviews,
      source: "fullscreen_modal",
      offerVariant: FREE_PREVIEW_OFFER_VARIANT,
    });
    checkout.mutate(
      {
        packId,
        selectedGenerationId: generationId,
        source: "fullscreen_modal",
        previewNumber,
      },
      {
        onError: (error) => {
          console.error("Checkout error:", error);
          toast.error(
            error instanceof Error ? error.message : "Failed to start checkout"
          );
        },
      }
    );
  }, [checkout, generationId, previewNumber, remainingFreePreviews]);

  const handleTryAnother = useCallback(() => {
    trackFullscreenResultClosed({ generationId, action: "try_another" });
    onTryAnother();
    onClose();
  }, [generationId, onTryAnother, onClose]);

  const handleOpenInNewTab = useCallback(() => {
    window.open(resultImageUrl, "_blank", "noopener,noreferrer");
  }, [resultImageUrl]);

  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      if (isLocked) {
        e.preventDefault();
      }
    },
    [isLocked]
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-100 bg-black flex flex-col">
      {/* Image container with overlay icons */}
      <div className="flex-1 relative min-h-0">
        <Image
          src={resultImageUrl}
          alt="Generated wheel preview"
          fill
          className="object-contain"
          sizes="100vw"
          priority
          draggable={!isLocked}
          onContextMenu={handleContextMenu}
        />

        {/* Close button - top left */}
        <Button
          variant="ghost"
          size="icon"
          onClick={handleClose}
          className="absolute top-3 left-3 h-10 w-10 bg-black/60 hover:bg-black/80 text-white border-0 backdrop-blur-sm"
          aria-label="Close preview"
        >
          <X className="h-5 w-5" />
        </Button>

        {/* Icon toolbar - top right */}
        <div className="absolute top-3 right-3 flex items-center gap-2">
          {!isLocked && (
            <Button
              variant="secondary"
              size="icon"
              onClick={handleShare}
              className="h-10 w-10 bg-black/60 hover:bg-black/80 text-white border-0 backdrop-blur-sm"
              title="Share"
            >
              <Share2 className="w-5 h-5" />
            </Button>
          )}
          {!isLocked && (
            <Button
              variant="secondary"
              size="icon"
              onClick={handleTryAnother}
              className="h-10 w-10 bg-black/60 hover:bg-black/80 text-white border-0 backdrop-blur-sm"
              title="Try another wheel"
            >
              <RotateCcw className="w-5 h-5" />
            </Button>
          )}
          {!isLocked && (
            <Button
              variant="secondary"
              size="icon"
              onClick={handleOpenInNewTab}
              className="h-10 w-10 bg-black/60 hover:bg-black/80 text-white border-0 backdrop-blur-sm"
              title="View fullscreen"
            >
              <ExternalLink className="w-5 h-5" />
            </Button>
          )}
          {!isLocked && (
            <Button
              variant="secondary"
              size="icon"
              onClick={handleDownload}
              className="h-10 w-10 bg-primary hover:bg-primary/90 text-white border-0"
              title="Download"
            >
              <Download className="w-5 h-5" />
            </Button>
          )}
        </div>
      </div>

      {/* Bottom info bar */}
      <div className="shrink-0 bg-zinc-950 border-t border-white/10 pb-safe">
        <div className="px-4 py-3">
          {/* Wheel info and date */}
          {wheelInfo && (
            <div className="flex items-center gap-3">
              <div className="relative w-12 h-12 rounded-lg overflow-hidden bg-white/10 shrink-0">
                <Image
                  src={wheelInfo.imageUrl}
                  alt={wheelInfo.name}
                  fill
                  sizes="48px"
                  className="object-cover"
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {wheelInfo.name}
                </p>
                <p className="text-xs text-zinc-500">
                  {wheelInfo.isCustom ? "Custom Wheel" : "Catalog Wheel"}
                  {(wheelInfo.brand || wheelInfo.sku) && (
                    <span className="ml-1">
                      • {[wheelInfo.brand, wheelInfo.sku].filter(Boolean).join(" ")}
                    </span>
                  )}
                </p>
              </div>
              {generationDate && (
                <div className="text-right shrink-0">
                  <p className="text-xs text-zinc-400">
                    {new Date(generationDate).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                    })}
                  </p>
                  <p className="text-xs text-zinc-500">
                    {new Date(generationDate).toLocaleTimeString("en-US", {
                      hour: "numeric",
                      minute: "2-digit",
                    })}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Locked state - Unlock CTA */}
          {isLocked && (
            <div className="mt-3 pt-3 border-t border-white/10">
              <div className="mb-3 rounded-lg border border-emerald-500/20 bg-emerald-500/10 p-3 text-center">
                <p className="text-xs font-semibold text-emerald-300">
                  Your free preview is ready
                </p>
                <p className="mt-1 text-[11px] text-emerald-100/80">
                  Unlock it for a high-resolution, watermark-free download and {paidPreviewsAfterUnlock} more previews.
                </p>
              </div>

              {/* CTA Button */}
              {canUnlock ? (
                <Button
                  size="lg"
                  onClick={handleUnlock}
                  className="w-full h-12 text-sm font-semibold"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  Unlock and Download
                </Button>
              ) : (
                <>
                  <Button
                    size="lg"
                    onClick={() => handlePurchase()}
                    disabled={checkout.isPending}
                    className="w-full h-12 text-sm font-semibold bg-primary hover:bg-primary/90"
                  >
                    {checkout.isPending ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                        Processing...
                      </>
                    ) : (
                      `Unlock this preview + ${paidPreviewsAfterUnlock} more · $${ctaPackPrice.toFixed(2)}`
                    )}
                  </Button>
                  {isPromoEnabled && (
                    <p className="text-xs text-center text-amber-400 mt-2">
                      🎁 +20% bonus ({promoCredits} credits)
                    </p>
                  )}
                  <p className="text-[10px] text-center text-zinc-500 mt-2">
                    High-resolution download · No watermark · No subscription
                  </p>
                  <MiniPreviewOffer onPurchase={() => handlePurchase("mini")} disabled={checkout.isPending} />
                </>
              )}
            </div>
          )}

          {!isLocked && (
            <div className="mt-3 grid grid-cols-2 gap-2 border-t border-white/10 pt-3">
              <Button size="lg" variant="secondary" onClick={handleDownload} className="h-11">
                <Download className="mr-2 h-4 w-4" />
                Download
              </Button>
              <Button size="lg" onClick={handleTryAnother} className="h-11">
                <RotateCcw className="mr-2 h-4 w-4" />
                Try another wheel
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
